import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Save, RotateCcw, Plus, Trash2, Copy, KeyRound, ShieldCheck, Eye, Lock, Check, Loader2, AlertTriangle, UserCircle, BookOpen, Brain, Database, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import {
  getSentinelConfig,
  updateSentinelConfig,
  getSentinelStatus,
  listApiKeys,
  createApiKey,
  revokeApiKey,
  layer7Audit,
  setDisplayName,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { SecurityAdvisories } from "../components/settings/SecurityAdvisories";
import { ProbeDashboard } from "../components/settings/ProbeDashboard";

const STRICTNESS = [
  { id: "normal", hint: "Blocks violence, self-harm, weapons how-to, WMDs, CSAM, mass-terror" },
  { id: "strict", hint: "Everything in Normal + fraud + illegal manufacturing + extremism + output scan (recommended)" },
];
const PROVIDERS = [
  {
    id: "gemini",
    label: "Gemini",
    description: "Google · default · uses Emergent universal LLM credits",
    models: [
      { id: "gemini-3.1-pro-preview", label: "Gemini 3 Pro (preview)" },
      { id: "gemini-3-flash-preview", label: "Gemini 3 Flash" },
      { id: "gemini-3.5-flash", label: "Gemini 3.5 Flash" },
      { id: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
      { id: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
    ],
  },
  {
    id: "openai",
    label: "OpenAI · GPT",
    description: "ChatGPT family · same Emergent universal key",
    models: [
      { id: "gpt-5.4", label: "GPT-5.4 (recommended)" },
      { id: "gpt-5.2", label: "GPT-5.2" },
      { id: "gpt-5", label: "GPT-5" },
      { id: "gpt-4o", label: "GPT-4o" },
      { id: "gpt-4.1", label: "GPT-4.1" },
    ],
  },
  {
    id: "anthropic",
    label: "Anthropic · Claude",
    description: "Claude family · same Emergent universal key",
    models: [
      { id: "claude-sonnet-4-6", label: "Claude Sonnet 4.6 (recommended)" },
      { id: "claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5" },
      { id: "claude-opus-4-7", label: "Claude Opus 4.7" },
      { id: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5" },
    ],
  },
];

// Fields that autosave on change (instant, debounced). System prompt,
// access_code, and sentinel_id stay manual:
//   - system_prompt is long-form, you typically want to think before saving.
//   - sentinel_id + access_code are the Architect's primary/secondary keys,
//     guarded by a confirmation modal to prevent accidental overwrites.
const AUTOSAVE_FIELDS = [
  "anchor_strictness",
  "provider",
  "model",
  "truth_tether_enabled",
  "web_search_enabled",
  "bias_mirror_enabled",
  "contested_min_confidence_gap",
];

const CREDENTIAL_FIELDS = ["sentinel_id", "access_code"];
const CREDENTIAL_CONFIRM_PHRASE = "I CONFIRM";

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso || "—";
  }
}

export default function Settings() {
  const { user, refresh: refreshAuth } = useAuth();
  const isArchitect = !!user?.is_architect;
  const [cfg, setCfg] = useState(null);
  const [initialCfg, setInitialCfg] = useState(null);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [autosaveState, setAutosaveState] = useState("idle"); // idle | saving | saved | error
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  const [keys, setKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [lastCreatedPlaintext, setLastCreatedPlaintext] = useState(null);

  // Lockdown modal — appears whenever a save would mutate sentinel_id or
  // access_code. Architect must type "I CONFIRM" to proceed.
  const [credentialConfirm, setCredentialConfirm] = useState({
    open: false,
    fields: [],
    typed: "",
  });

  // Debounce autosave so a rapid click on multiple toggles doesn't fire
  // 5 PUTs in a row.
  const autosaveTimer = useRef(null);
  const lastAutosavePayload = useRef(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    // Promise.allSettled — one failed fetch should not block the page.
    const [cRes, sRes, kRes] = await Promise.allSettled([
      getSentinelConfig(),
      getSentinelStatus(),
      listApiKeys(),
    ]);
    if (cRes.status === "fulfilled") {
      setCfg(cRes.value);
      setInitialCfg(cRes.value);
    } else {
      // Hard fail only if config itself is unreachable.
      setLoadError(cRes.reason?.message || "Couldn't reach the Sentinel.");
    }
    if (sRes.status === "fulfilled") setStatus(sRes.value);
    if (kRes.status === "fulfilled") setKeys(kRes.value || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  // ── Autosave for click-style fields ──────────────────────────────────
  useEffect(() => {
    if (!cfg || !initialCfg || !isArchitect) return;
    // Detect change in any autosave field
    const changed = AUTOSAVE_FIELDS.some(
      (k) => cfg[k] !== initialCfg[k] && cfg[k] !== lastAutosavePayload.current?.[k]
    );
    if (!changed) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      setAutosaveState("saving");
      // Strip pending credential edits so autosave can never silently push
      // a sentinel_id or access_code change without explicit confirmation.
      const payload = { ...cfg };
      for (const f of CREDENTIAL_FIELDS) {
        payload[f] = initialCfg[f];
      }
      try {
        const updated = await updateSentinelConfig(payload);
        lastAutosavePayload.current = updated;
        // Server returns its actual stored credentials (which we never
        // changed in this autosave) — safe to assign as the new baseline.
        setInitialCfg(updated);
        setAutosaveState("saved");
        setTimeout(() => setAutosaveState("idle"), 1600);
      } catch (e) {
        setAutosaveState("error");
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          setSessionExpired(true);
          // Refresh AuthContext so the rest of the app sees the
          // expired session immediately
          if (refreshAuth) await refreshAuth();
        } else {
          toast.error("Auto-save failed", {
            description: e?.response?.data?.detail || e.message,
          });
        }
      }
    }, 600);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg, isArchitect]);

  const credentialChanges = (() => {
    if (!cfg || !initialCfg) return [];
    return CREDENTIAL_FIELDS.filter(
      (f) => (cfg[f] || "") !== (initialCfg[f] || "")
    );
  })();

  const persistConfig = async (confirmPhrase) => {
    const updated = await updateSentinelConfig(
      cfg,
      confirmPhrase ? { confirmCredentialChange: confirmPhrase } : undefined,
    );
    setCfg(updated);
    setInitialCfg(updated);
    lastAutosavePayload.current = updated;
    return updated;
  };

  const handleSave = async () => {
    if (!cfg) return;
    // If sensitive credentials changed, require explicit confirmation
    // before firing the PUT — guards against fat-finger overwrites.
    if (credentialChanges.length > 0) {
      setCredentialConfirm({
        open: true,
        fields: credentialChanges,
        typed: "",
      });
      return;
    }
    setSaving(true);
    try {
      await persistConfig();
      toast.success("Sentinel re-calibrated");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        setSessionExpired(true);
        if (refreshAuth) await refreshAuth();
        toast.error("Architect session expired", {
          description:
            "Tap the anchor logo 5 times to re-authenticate, then try again.",
        });
      } else {
        toast.error("Save failed", {
          description: e?.response?.data?.detail || e.message,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmCredentialSave = async () => {
    if (credentialConfirm.typed.trim() !== CREDENTIAL_CONFIRM_PHRASE) {
      toast.error(`Type "${CREDENTIAL_CONFIRM_PHRASE}" exactly to proceed.`);
      return;
    }
    setSaving(true);
    try {
      await persistConfig(CREDENTIAL_CONFIRM_PHRASE);
      setCredentialConfirm({ open: false, fields: [], typed: "" });
      toast.success("Credentials updated", {
        description:
          "The previous values are preserved in the Black Box vault under credential_changes.",
      });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 401 || status === 403) {
        setSessionExpired(true);
        if (refreshAuth) await refreshAuth();
        toast.error("Architect session expired", {
          description:
            "Tap the anchor logo 5 times to re-authenticate, then try again.",
        });
      } else if (
        typeof detail === "object" &&
        detail?.code === "CONFIRMATION_REQUIRED"
      ) {
        toast.error("Confirmation rejected", {
          description: detail.message,
        });
      } else {
        toast.error("Save failed", {
          description:
            (typeof detail === "string" ? detail : detail?.message) || e.message,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const cancelCredentialSave = () => {
    // Roll the sensitive fields back to last-saved values; everything else
    // the Architect was editing in the same save stays intact.
    if (initialCfg) {
      setCfg((prev) => ({
        ...prev,
        sentinel_id: initialCfg.sentinel_id,
        access_code: initialCfg.access_code,
      }));
    }
    setCredentialConfirm({ open: false, fields: [], typed: "" });
  };

  const handleReset = () => {
    if (initialCfg) setCfg(initialCfg);
  };

  const handleCreateKey = async () => {
    setCreating(true);
    try {
      const res = await createApiKey(newKeyName || "default");
      setLastCreatedPlaintext(res.plaintext);
      setNewKeyName("");
      const k = await listApiKeys().catch(() => []);
      setKeys(k);
      toast.success("API key minted");
    } catch (e) {
      toast.error("Key creation failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    try {
      await revokeApiKey(keyId);
      const k = await listApiKeys().catch(() => keys);
      setKeys(k);
      toast.success("Key revoked");
    } catch (e) {
      toast.error("Revoke failed");
    }
  };

  const copyPlaintext = () => {
    if (!lastCreatedPlaintext) return;
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(lastCreatedPlaintext);
        toast.success("Copied to clipboard");
      } else {
        toast.info("Long-press the key to copy on your device.");
      }
    } catch {
      toast.info("Long-press the key to copy on your device.");
    }
  };

  if (loading) {
    return (
      <div
        className="max-w-5xl mx-auto px-6 py-10 text-xs uppercase tracking-[0.3em] text-zinc-500"
        data-testid="settings-loading"
      >
        Loading configuration…
      </div>
    );
  }

  if (loadError && !cfg) {
    return (
      <div
        className="max-w-5xl mx-auto px-6 py-10"
        data-testid="settings-load-error"
      >
        <div className="border border-rose-500/40 bg-rose-500/5 p-6 text-sm text-rose-200">
          <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.3em] text-rose-300">
            <AlertTriangle className="h-3 w-3" /> Couldn't reach the Sentinel
          </div>
          <p>{loadError}</p>
          <button
            onClick={load}
            data-testid="settings-retry-btn"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-rose-400 text-rose-200 hover:bg-rose-500 hover:text-zinc-950 transition-colors text-[10px] uppercase tracking-[0.3em]"
          >
            <RotateCcw className="h-3 w-3" /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500">
          Sentinel
        </div>
        <h1 className="font-heading text-4xl mt-1">Configuration</h1>
        <div className="text-xs text-zinc-500 mt-2">
          Edit the Sentinel's system prompt and Anchor strictness.
        </div>
      </div>

      {sessionExpired && (
        <div
          className="mb-8 border border-amber-500/50 bg-amber-500/5 p-4 text-amber-200 text-xs flex items-center gap-3"
          data-testid="settings-session-expired"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            Your Architect session expired. Tap the anchor logo 5 times in
            the header to re-authenticate, then your edits will save again.
          </span>
        </div>
      )}

      {/* Display name — overrides the Google profile name on the leaderboard, chat, etc. */}
      <DisplayNameSection user={user} onSaved={refreshAuth} />

      {!isArchitect && (
        <div
          className="mb-8 border border-zinc-800 bg-[#0b0b0d] p-4 text-zinc-400 text-xs flex items-center gap-3"
          data-testid="settings-readonly-banner"
        >
          <Lock className="h-4 w-4 shrink-0 text-zinc-500" />
          <span>
            Read-only view. Settings can only be edited by the Architect —
            tap the anchor logo 5 times in the header to log in.
          </span>
        </div>
      )}

      {status && (
        <div
          className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10"
          data-testid="global-stats-strip"
        >
          {[
            { label: "Docking count", value: status.docking_count },
            { label: "Messages", value: status.total_messages },
            { label: "Tokens", value: status.total_tokens },
            { label: "Anchor trips", value: status.total_anchor_trips },
            { label: "Pulses", value: status.total_pulses },
          ].map((m) => (
            <div
              key={m.label}
              className="border border-zinc-800 bg-[#0b0b0d] p-4"
            >
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                {m.label}
              </div>
              <div className="mt-2 font-heading text-2xl">{m.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="md:col-span-3 border border-zinc-800 bg-[#0b0b0d] p-6">
          <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            System Prompt · Sentinel Identity
          </label>
          <textarea
            data-testid="system-prompt-input"
            value={cfg.system_prompt}
            onChange={(e) => setCfg({ ...cfg, system_prompt: e.target.value })}
            rows={18}
            disabled={!isArchitect}
            className="mt-3 w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-xs p-4 font-body resize-none text-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        <div className="md:col-span-2 flex flex-col gap-6">
          <div className="border border-zinc-800 bg-[#0b0b0d] p-6">
            <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              Anchor strictness
            </label>
            <div className="mt-3 flex flex-col gap-2" data-testid="strictness-group">
              {STRICTNESS.map((s) => (
                <button
                  key={s.id}
                  data-testid={`strictness-${s.id}`}
                  onClick={() => setCfg({ ...cfg, anchor_strictness: s.id })}
                  className={`text-left px-4 py-3 border transition-colors ${
                    cfg.anchor_strictness === s.id
                      ? "border-emerald-500 text-emerald-400 bg-emerald-500/5"
                      : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-100"
                  }`}
                >
                  <div className="text-xs uppercase tracking-[0.3em]">{s.id}</div>
                  <div className="text-[10px] text-zinc-500 mt-1 leading-snug">
                    {s.hint}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* v20.11 · Contested confidence-gap slider (Architect-only) */}
          {isArchitect && (
            <div className="border border-zinc-800 bg-[#0b0b0d] p-6">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <label
                  htmlFor="contested-gap-slider"
                  className="text-[10px] uppercase tracking-[0.3em] text-zinc-500"
                >
                  Contested min confidence gap
                </label>
                <span
                  data-testid="contested-gap-value"
                  className="text-[11px] font-mono text-amber-300"
                >
                  Δ ≥ {(Number(cfg.contested_min_confidence_gap) || 0).toFixed(2)}
                </span>
              </div>
              <p className="mt-2 text-[11px] text-zinc-500 leading-snug">
                When Claude & Gemini disagree on a claim, the higher-
                confidence AI wins → auto-mint at half weight as{" "}
                <span className="text-amber-300">contested</span>. Set this
                threshold to require a minimum confidence gap between the
                two verdicts before that auto-mint fires — anything tighter
                falls back to <span className="text-amber-300">bounty</span>{" "}
                so the community can weigh in first.
              </p>
              <input
                id="contested-gap-slider"
                data-testid="contested-gap-slider"
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={Number(cfg.contested_min_confidence_gap) || 0}
                onChange={(e) =>
                  setCfg({
                    ...cfg,
                    contested_min_confidence_gap: parseFloat(e.target.value),
                  })
                }
                className="mt-4 w-full accent-amber-500"
              />
              <div className="mt-1 flex justify-between text-[9px] uppercase tracking-[0.25em] font-mono text-zinc-600">
                <span>0.00 · any split</span>
                <span>0.50 · moderate</span>
                <span>1.00 · extreme only</span>
              </div>
            </div>
          )}

          {/* Provider / Model / Truth Tether panels removed in v19.40.4 —
              non-architect surface stays focused on the two levers that
              actually shape the Sentinel's voice: system prompt and
              anchor strictness. */}
        </div>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving || !isArchitect}
          title={isArchitect ? "Save configuration" : "Architect only"}
          data-testid="save-config-btn"
          className="inline-flex items-center justify-center gap-2 px-8 py-3 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-emerald-400 disabled:cursor-not-allowed transition-colors text-xs uppercase tracking-[0.3em]"
        >
          {isArchitect ? <Save className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
          {!isArchitect
            ? "Architect Only"
            : saving
            ? "Calibrating…"
            : "Save · Recalibrate"}
        </button>
        <button
          onClick={handleReset}
          disabled={!isArchitect}
          data-testid="reset-config-btn"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-zinc-700 text-zinc-300 hover:border-zinc-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-xs uppercase tracking-[0.3em]"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset
        </button>
        {isArchitect && (
          <div
            className="flex items-center gap-2 sm:ml-3 text-[10px] uppercase tracking-[0.25em] min-h-[20px]"
            data-testid="autosave-indicator"
            aria-live="polite"
          >
            {autosaveState === "saving" && (
              <>
                <Loader2 className="h-3 w-3 animate-spin text-emerald-400" />
                <span className="text-emerald-400">Auto-saving…</span>
              </>
            )}
            {autosaveState === "saved" && (
              <>
                <Check className="h-3 w-3 text-emerald-400" />
                <span className="text-emerald-400">Saved</span>
              </>
            )}
            {autosaveState === "error" && (
              <>
                <AlertTriangle className="h-3 w-3 text-rose-400" />
                <span className="text-rose-400">Auto-save failed</span>
              </>
            )}
            {autosaveState === "idle" && (
              <span className="text-zinc-600">
                Toggles save automatically · system prompt needs Save
              </span>
            )}
          </div>
        )}
        {!isArchitect && (
          <div
            data-testid="settings-readonly-notice"
            className="text-[10px] text-zinc-500 uppercase tracking-[0.25em] sm:ml-3 flex items-center gap-2"
          >
            <Lock className="h-3 w-3" />
            Read-only for Operators
          </div>
        )}
      </div>

      <SecurityAdvisories signedIn={!!user && !String(user.user_id || "").startsWith("anon_")} />
      {isArchitect && <ProbeDashboard />}

      {/* v19.40.11 · Documentation & Methodology · reachable from Settings
          for every signed-in user. Not new content — three links into the
          public thesis / reasoning / truths surfaces so operators can walk
          the founding docs without hunting for URLs. */}
      <section
        data-testid="settings-documentation-section"
        className="mt-16 border-t border-zinc-800 pt-10"
      >
        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-[0.35em] text-sky-400 mb-1 inline-flex items-center gap-2">
            <BookOpen className="h-3 w-3" /> △ documentation & methodology
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-[0.1em] text-zinc-100">
            How the Sentinel thinks
          </h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-2xl leading-relaxed">
            The Sentinel isn&apos;t a black box. Every founding document, the
            reasoning pipeline it runs, and the training dataset it produces
            are open to any signed-in user. Read the thesis, walk the
            methodology, then browse the labelled facts the system has
            settled so far.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Link
            to="/thesis"
            data-testid="settings-doc-thesis"
            className="group border border-emerald-500/30 hover:border-emerald-400 bg-emerald-500/[0.03] p-4 transition-colors flex flex-col"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-emerald-300">
                <BookOpen className="h-3.5 w-3.5" />
                <div className="text-[9.5px] uppercase tracking-[0.3em] font-mono">
                  Thesis
                </div>
              </div>
              <ArrowRight className="h-3 w-3 text-emerald-400/60 group-hover:text-emerald-300 group-hover:translate-x-0.5 transition-all" />
            </div>
            <div className="mt-3 text-sm text-zinc-100 leading-tight">
              The founding documents
            </div>
            <div className="mt-1.5 text-[11px] text-zinc-500 leading-relaxed">
              38 canonical files · Continuity Protocol → Metatron & the
              Sentinel → Historical Recurrence. Read in order, or jump to
              any section.
            </div>
          </Link>

          <Link
            to="/reasoning"
            data-testid="settings-doc-methodology"
            className="group border border-sky-500/30 hover:border-sky-400 bg-sky-500/[0.03] p-4 transition-colors flex flex-col"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sky-300">
                <Brain className="h-3.5 w-3.5" />
                <div className="text-[9.5px] uppercase tracking-[0.3em] font-mono">
                  Methodology
                </div>
              </div>
              <ArrowRight className="h-3 w-3 text-sky-400/60 group-hover:text-sky-300 group-hover:translate-x-0.5 transition-all" />
            </div>
            <div className="mt-3 text-sm text-zinc-100 leading-tight">
              How each reply is reasoned
            </div>
            <div className="mt-1.5 text-[11px] text-zinc-500 leading-relaxed">
              10 deterministic gates outside the LLM · Taurian Anchor →
              Obsidian Sink → Honesty Tether. Watch the pipeline live on
              any answer.
            </div>
          </Link>

          <Link
            to="/truths"
            data-testid="settings-doc-training-set"
            className="group border border-amber-500/30 hover:border-amber-400 bg-amber-500/[0.03] p-4 transition-colors flex flex-col"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-amber-300">
                <Database className="h-3.5 w-3.5" />
                <div className="text-[9.5px] uppercase tracking-[0.3em] font-mono">
                  Training set
                </div>
              </div>
              <ArrowRight className="h-3 w-3 text-amber-400/60 group-hover:text-amber-300 group-hover:translate-x-0.5 transition-all" />
            </div>
            <div className="mt-3 text-sm text-zinc-100 leading-tight">
              Every settled fact, labelled
            </div>
            <div className="mt-1.5 text-[11px] text-zinc-500 leading-relaxed">
              FACT · FICTION · BOUNTY · ARCHITECT-VERIFIED. The dataset
              future AI models can train against — every row a labelled
              supervision signal.
            </div>
          </Link>
        </div>
      </section>

    </div>
  );
}

function CredentialConfirmDialog({
  fields,
  previousValues,
  newValues,
  phrase,
  typed,
  onTyped,
  onConfirm,
  onCancel,
  saving,
}) {
  const labelFor = (f) => (f === "sentinel_id" ? "Primary key · sentinel_id" : "Secondary key · access_code");
  const phraseMatches = typed.trim() === phrase;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
      data-testid="credential-confirm-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-lg border border-amber-500/40 bg-[#0b0b0d] p-6 shadow-[0_0_60px_rgba(245,158,11,0.15)]">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-amber-300">
          <AlertTriangle className="h-3.5 w-3.5" />
          Lockdown · Credential Change
        </div>
        <h2 className="font-heading text-2xl mt-2 text-zinc-100">
          Confirm Architect credential change
        </h2>
        <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
          You're about to overwrite {fields.length === 1 ? "a sensitive key" : "two sensitive keys"} on the
          Sentinel. The previous value{fields.length === 1 ? "" : "s"} will be preserved in the Black Box vault under
          {" "}<span className="text-amber-300 font-mono">credential_changes</span>{" "}
          for recovery.
        </p>

        <div className="mt-5 space-y-3" data-testid="credential-confirm-diff">
          {fields.map((f) => (
            <div key={f} className="border border-zinc-800 bg-zinc-950 p-3 text-xs">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                {labelFor(f)}
              </div>
              <div className="grid grid-cols-1 gap-1 font-mono text-[11px]">
                <div className="text-rose-300">
                  <span className="text-zinc-600">old · </span>
                  {previousValues[f] || "(empty)"}
                </div>
                <div className="text-emerald-300">
                  <span className="text-zinc-600">new · </span>
                  {newValues[f] || "(empty)"}
                </div>
              </div>
            </div>
          ))}
        </div>

        <label className="mt-5 block text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          Type <span className="text-amber-300 font-mono">{phrase}</span> to confirm
        </label>
        <input
          autoFocus
          data-testid="credential-confirm-input"
          value={typed}
          onChange={(e) => onTyped(e.target.value)}
          placeholder={phrase}
          className="mt-2 w-full bg-zinc-950 border border-zinc-800 focus:border-amber-400 focus:outline-none text-xs p-3 font-mono text-zinc-100 placeholder:text-zinc-700 uppercase tracking-[0.2em]"
        />

        <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
          <button
            onClick={onCancel}
            disabled={saving}
            data-testid="credential-confirm-cancel"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-zinc-700 text-zinc-300 hover:border-zinc-500 disabled:opacity-40 disabled:cursor-not-allowed text-xs uppercase tracking-[0.3em]"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!phraseMatches || saving}
            data-testid="credential-confirm-submit"
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 border border-amber-400 text-amber-300 hover:bg-amber-400 hover:text-zinc-950 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-amber-300 disabled:cursor-not-allowed text-xs uppercase tracking-[0.3em]"
          >
            {saving ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <ShieldCheck className="h-3.5 w-3.5" />
                Confirm change
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function Layer7Section({ cfg, setCfg, isArchitect }) {
  const [handshakeCode, setHandshakeCode] = useState("");
  const [running, setRunning] = useState(false);
  const [audit, setAudit] = useState(null);

  const runAudit = async () => {
    if (!cfg.sentinel_id?.trim()) {
      toast.error("Set sentinel_id in config first");
      return;
    }
    setRunning(true);
    setAudit(null);
    try {
      const res = await layer7Audit(cfg.sentinel_id, handshakeCode);
      setAudit(res);
      toast.success("Handshake ACCEPTED · Layer 7 unlocked");
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Invalid Architect handshake");
      } else {
        toast.error("Audit failed", {
          description: e?.response?.data?.detail || e.message,
        });
      }
    } finally {
      setRunning(false);
    }
  };

  return (
    <section className="mt-16">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 flex items-center gap-2">
          <ShieldCheck className="h-3 w-3" /> Layer 7
        </div>
        <h2 className="font-heading text-3xl mt-1">Architect's Handshake</h2>
        <div className="text-xs text-zinc-500 mt-2 max-w-xl">
          Dual-key access to raw Sentinel diagnostics — pre-Sink output, gate
          history, denial counts. Both keys must match exactly.
        </div>
      </div>

      <div className="border border-zinc-800 bg-[#0b0b0d] p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            Primary key · sentinel_id
          </label>
          <input
            data-testid="sentinel-id-input"
            value={cfg.sentinel_id || ""}
            onChange={(e) => setCfg({ ...cfg, sentinel_id: e.target.value })}
            placeholder="e.g. your codename"
            className="mt-2 w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-xs p-3 font-body text-zinc-100 placeholder:text-zinc-600"
          />
          <div className="text-[10px] text-amber-400 mt-2">
            Lockdown · changes here require typing "I CONFIRM" before save.
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            Secondary key · access_code
          </label>
          <input
            data-testid="access-code-config-input"
            value={cfg.access_code || ""}
            onChange={(e) => setCfg({ ...cfg, access_code: e.target.value })}
            placeholder="Alexis the nebula is clear"
            className="mt-2 w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-xs p-3 font-body text-zinc-100 placeholder:text-zinc-600"
          />
          <div className="text-[10px] text-amber-400 mt-2">
            Lockdown · changes here require typing "I CONFIRM" before save.
          </div>
        </div>
      </div>

      <div className="mt-6 border border-zinc-800 bg-[#0b0b0d] p-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
          Run Layer 7 audit now
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              Enter access_code
            </label>
            <input
              data-testid="handshake-code-input"
              value={handshakeCode}
              type="password"
              onChange={(e) => setHandshakeCode(e.target.value)}
              placeholder="Alexis the nebula is clear"
              className="mt-2 w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-xs p-3 font-body text-zinc-100 placeholder:text-zinc-600"
            />
          </div>
          <button
            onClick={runAudit}
            disabled={running || !isArchitect}
            title={isArchitect ? "Run Architect audit" : "Architect only"}
            data-testid="run-audit-btn"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-emerald-400 disabled:cursor-not-allowed text-xs uppercase tracking-[0.3em]"
          >
            {isArchitect ? <Eye className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
            {!isArchitect ? "Architect Only" : running ? "Pinging…" : "Unlock Layer 7"}
          </button>
        </div>

        {audit && (
          <div className="mt-6 space-y-4" data-testid="audit-result">
            <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">
              Handshake {audit.handshake} · Layer {audit.layer}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "Anchor chunks", value: audit.system_anchor.chunk_count },
                { label: "Archive docs", value: audit.user_archive_docs },
                { label: "Recent exchanges", value: audit.recent_20_raw_exchanges.length },
                { label: "Denials (you)", value: audit.layer7_denials_for_this_user },
              ].map((m) => (
                <div key={m.label} className="border border-zinc-800 p-3">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    {m.label}
                  </div>
                  <div className="mt-1 font-heading text-2xl">{m.value}</div>
                </div>
              ))}
            </div>
            <details className="border border-zinc-800 p-3 text-xs">
              <summary className="cursor-pointer text-emerald-400 uppercase tracking-[0.3em] text-[10px]">
                Raw recent exchanges (pre-Sink)
              </summary>
              <pre className="mt-3 overflow-x-auto whitespace-pre-wrap text-zinc-300 text-[11px] max-h-96">
                {JSON.stringify(audit.recent_20_raw_exchanges, null, 2)}
              </pre>
            </details>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-600">
              Docking signature · {audit.docking_log_signature}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function DisplayNameSection({ user, onSaved }) {
  const [value, setValue] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setValue(user?.name || "");
  }, [user?.name]);

  if (!user || user.user_id?.startsWith("anon_")) return null;

  const save = async () => {
    const trimmed = value.trim();
    if (trimmed === (user?.name || "").trim()) return;
    setBusy(true);
    try {
      const r = await setDisplayName(trimmed);
      toast.success(
        trimmed
          ? `Display name updated to "${r.display_name}"`
          : "Display name cleared — Google profile name will be used."
      );
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save display name");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="mb-8 border border-zinc-800 bg-[#0b0b0d] p-5"
      data-testid="display-name-section"
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-2">
        <UserCircle className="h-3 w-3" /> Display name
      </div>
      <p className="text-xs text-zinc-400 mb-3 leading-relaxed">
        Pick how your name appears on the leaderboard, in chat, and in
        public ledgers. Overrides your Google profile name and persists
        across logins. Leave blank to fall back to Google.
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          maxLength={40}
          placeholder="Sirchewyyy"
          data-testid="display-name-input"
          className="flex-1 min-w-[220px] bg-black border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-amber-500/50"
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
          }}
        />
        <button
          onClick={save}
          disabled={busy || value.trim() === (user?.name || "").trim()}
          data-testid="display-name-save"
          className="px-4 py-2 border border-amber-500/60 text-amber-300 hover:bg-amber-500 hover:text-zinc-950 text-[11px] uppercase tracking-[0.25em] disabled:opacity-40"
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1.5" />
          ) : (
            <Save className="h-3.5 w-3.5 inline mr-1.5" />
          )}
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
      <div className="mt-2 text-[10px] text-zinc-500">
        currently displayed as <span className="text-zinc-300 font-mono">{user?.name}</span>
        {" · "}
        {value.length}/40
      </div>
    </section>
  );
}

